import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mic, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const HowItWorks = () => {
  const steps = [
    {
      icon: Phone,
      number: "1",
      title: "Le patient appelle",
      description: "Votre patient compose votre numéro Fruitycall et laisse un message vocal."
    },
    {
      icon: Mic,
      number: "2",
      title: "Fruitycall enregistre et transcrit",
      description: "Le message est automatiquement enregistré et transcrit en texte de manière sécurisée."
    },
    {
      icon: FileText,
      number: "3",
      title: "Vous consultez quand vous le souhaitez",
      description: "Accédez à vos messages transcrits via votre espace professionnel, quand cela vous arrange."
    }
  ];
  
  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      
      <main className="flex-1">
        {/* Hero */}
        <section className="section-spacing bg-gradient-to-br from-primary/5 via-background to-secondary/10">
          <div className="container-custom text-center">
            <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Comment ça marche ?
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Un processus simple en trois étapes pour gérer vos messages médicaux efficacement
            </p>
          </div>
        </section>
        
        {/* Steps */}
        <section className="section-spacing">
          <div className="container-custom">
            <div className="grid gap-12 lg:gap-16">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isEven = index % 2 === 0;
                
                return (
                  <div key={index} className={`grid gap-8 lg:grid-cols-2 lg:gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Content */}
                    <div className={`space-y-4 ${!isEven ? 'lg:order-2' : ''}`}>
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
                        {step.number}
                      </div>
                      <h2 className="text-3xl font-bold">{step.title}</h2>
                      <p className="text-lg text-muted-foreground">{step.description}</p>
                    </div>
                    
                    {/* Visual */}
                    <Card className={`border-2 ${!isEven ? 'lg:order-1' : ''}`}>
                      <CardContent className="flex items-center justify-center p-16">
                        <div className="rounded-full bg-primary/10 p-8">
                          <Icon className="h-24 w-24 text-primary" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        
        {/* Benefits */}
        <section className="section-spacing bg-muted/30">
          <div className="container-custom">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-12">
                <div className="grid gap-8 lg:grid-cols-2 lg:gap-16 items-center">
                  <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Njg3Mzg1MjZ8MA&ixlib=rb-4.1.0&q=85"
                    alt="Stéthoscope avec ordinateur"
                    className="rounded-lg shadow-lg"
                  />
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold">Conçu pour les professionnels de santé</h2>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="mt-1 rounded-full bg-success/10 p-1">
                          <div className="h-2 w-2 rounded-full bg-success" />
                        </div>
                        <span className="text-muted-foreground">Aucune installation nécessaire</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="mt-1 rounded-full bg-success/10 p-1">
                          <div className="h-2 w-2 rounded-full bg-success" />
                        </div>
                        <span className="text-muted-foreground">Accessible sur ordinateur et smartphone</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="mt-1 rounded-full bg-success/10 p-1">
                          <div className="h-2 w-2 rounded-full bg-success" />
                        </div>
                        <span className="text-muted-foreground">Sécurité et confidentialité garanties</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="mt-1 rounded-full bg-success/10 p-1">
                          <div className="h-2 w-2 rounded-full bg-success" />
                        </div>
                        <span className="text-muted-foreground">Support dédié aux professionnels de santé</span>
                      </li>
                    </ul>
                    <Link to="/login">
                      <Button size="lg">Commencer maintenant</Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      
      <PublicFooter />
    </div>
  );
};

export default HowItWorks;